import { Injectable, computed, signal } from '@angular/core';
import { LoginResponse } from './auth.model';

const StorageKey = 'asb.auth.session';

interface StoredSession {
  accessToken: string;
  expiresAtUtc: string;
  role: string;
  requiresPasswordChange: boolean;
}

/**
 * Holds the current login session as signals, persisted to `sessionStorage` so a page reload
 * doesn't log the user out (but a new browser session does - tokens deliberately don't survive
 * across browser restarts).
 */
@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly session = signal<StoredSession | null>(readStoredSession());

  /** The current JWT access token, or `null` when logged out. */
  readonly accessToken = computed(() => this.session()?.accessToken ?? null);

  /** The internal code of the current user's role ("admin" or "debutant"), or `null`. */
  readonly role = computed(() => this.session()?.role ?? null);

  /**
   * The current user's own id, read from the access token's "sub" claim (see
   * `JwtTokenService.CreateAccessToken` in `ASB.API`). `null` when logged out or the token is
   * malformed.
   */
  readonly userId = computed(() => decodeUserIdFromToken(this.accessToken()));

  /** Whether the current account must change its password before doing anything else. */
  readonly requiresPasswordChange = computed(() => this.session()?.requiresPasswordChange ?? false);

  /** Whether a session is currently stored. Does not check token expiry client-side. */
  readonly isAuthenticated = computed(() => this.session() !== null);

  /** Whether the current user is an admin. */
  readonly isAdmin = computed(() => this.role() === 'admin');

  /** Whether the current user is a debutant. */
  readonly isDebutant = computed(() => this.role() === 'debutant');

  /**
   * Stores a freshly issued login session.
   */
  setSession(response: LoginResponse): void {
    const stored: StoredSession = {
      accessToken: response.accessToken,
      expiresAtUtc: response.expiresAtUtc,
      role: response.role,
      requiresPasswordChange: response.requiresPasswordChange,
    };

    this.session.set(stored);
    writeStoredSession(stored);
  }

  /**
   * Clears the forced password-change flag after the user has changed their password.
   */
  clearPasswordChangeRequirement(): void {
    const current = this.session();

    if (!current) {
      return;
    }

    const updated: StoredSession = { ...current, requiresPasswordChange: false };
    this.session.set(updated);
    writeStoredSession(updated);
  }

  /**
   * Ends the current session, e.g. on logout or when the API reports an expired/invalid token.
   */
  clear(): void {
    this.session.set(null);

    try {
      sessionStorage.removeItem(StorageKey);
    } catch {
      // storage unavailable (private browsing, disabled cookies, etc.) - nothing to clean up
    }
  }
}

function readStoredSession(): StoredSession | null {
  try {
    const raw = sessionStorage.getItem(StorageKey);
    return raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    return null;
  }
}

function writeStoredSession(session: StoredSession): void {
  try {
    sessionStorage.setItem(StorageKey, JSON.stringify(session));
  } catch {
    // storage unavailable - the session just won't survive a reload
  }
}

function decodeUserIdFromToken(token: string | null): number | null {
  if (!token) {
    return null;
  }

  try {
    const payload = token.split('.')[1];
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { sub?: string };
    const userId = Number(json.sub);
    return Number.isFinite(userId) ? userId : null;
  } catch {
    return null;
  }
}
